import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import { arrayMove } from "react-sortable-hoc";

// Components
import GeneralLoader from "../../GeneralLoader";
import DeleteConfirmationDialog from "../../utils/dialogs/DeleteConfirmationDialog";
import ErrorWrap from "../../utils/error-success/ErrorWrap";
import SuccessWrap from "../../utils/error-success/SuccessWrap";
import Button from "../../utils/Button";
import ReviewForm from "./form-components/ReviewForm";
import ErrorDialog from "../../utils/dialogs/ErrorDialog";

// State
import { userTokenUpdate, userLogout } from "../../../store/actions/user";
import { getReviewRecord, updateDocuments } from "../../../store/actions/reviews";
import { getSectors } from "../../../store/actions/sectors";
import { getOrganizations } from "../../../store/actions/organizations";
import { setpageTitle } from "../../../store/actions/siteInfo";

// Utils
import { formatMomentObject, formatMomentString } from "../../../utils/dateHelpers";
import { deleteTempImages } from "../../../utils/uploadHelpers";
import {
	updateSelectedValue,
	handleKeyPress,
	handleCheckboxClick,
	editorChange,
	handleDateChange,
	handleCheckboxClickArray
} from "../../../utils/forms/inputHandling";

import { DispatchSaveReviewer } from "../../../store/actions/reviewers";
import ManagersAlert from "../../utils/ManagersAlert";

class AdminReviewsForm extends Component {
	constructor(props) {
		super(props);

		if (props.match.path === "/admin/reviews/add") {
			props.setpageTitle("Add Review");
		} else {
			props.setpageTitle("Edit Review");
		}

		// We need to define all data because it needs to exist for controlled inputs when data is "new"
		this.state = {
			data: {
				date_added: null,
				date_edited: null,
				id: !!props.match.params.id ? props.match.params.id : null,
				organization_id: "",
				operating_expenditure: "",
				board_chair_fname: "",
				board_chair_lname: "",
				board_chair_email: "",
				exec_director_fname: "",
				exec_director_lname: "",
				exec_director_email: "",
				lead_fname: "",
				lead_lname: "",
				lead_email: "",
				status_notes: "",
				cca_manager_specialist: "",
				main_sector: null,
				french_aspect_required: 0,
				accreditation_anniversary: null,
				extended_to: "",
				review_team_rating: "",
				cca_support_rating: "",
				accreditation_process_rating: "",
				site_visit_notes: "",
				am_pm_start: "",
				location: "",
				site_visit_starts: null,
				site_visit_ends: null,
				team_arrives: null,
				team_departs: null,
				other_modules: [],
				completed: 0,
				reviewers: {}
			},
			isLoading: !!props.match.params.id ? true : false,
			addSucccess: !!props.location.state ? props.location.state.successMessage : "",
			updateLoading: false,
			updateErrors: {},
			updateSuccess: "",
			showDeleteConfirmationDialog: false,
			deletionInProgress: false,
			deleteErrors: "",
			deleteSuccess: "",

			// Document Uploads
			promiseCollection: [],
			documentsInProgress: false,
			documentsError: null,
			documentsSuccess: null,
			allowRetry: false,
			showErrorDialog: false,
			showFileDeleteConfirmationDialog: false,
			deleteItem: {}
		};

		// Remove state from history object (success messages, etc)
		if (!!props.location.state) {
			props.history.replace({
				pathname: props.history.location.pathname
			});
		}
	}

	// ===================================================================================
	//  LIFE CYCLE
	// ===================================================================================
	componentDidMount() {
		if (this.state.data.id) {
			this.loadData();
		} else {
			this.formatRecordData(this.state);
		}

		this.props.getSectors();
		this.props.getOrganizations();
	}

	loadData() {
		this.props.getReviewRecord(this.state.data.id, true).then(resp => {
			if (resp.status === 403) {
				this.props.userLogout("admin");
				return;
			}

			// If no response, bail
			if (!resp || !resp.data.id) {
				this.setState({ isLoading: false });
				return;
			}

			const data = this.formatRecordData(resp.data);

			// Set the component's data
			this.setState({ data, isLoading: false });

			// The above is used to display it in other areas. didn't have time to fix this because i have to get back
			// to working on digital warehouse didn't want mark to tell me why did it take me so long to fix it
			// DispatchSaveReviewer(data);
		});
	}

	formatRecordData = data => {
		console.log(data);
		// Ensure dates are valid
		const date_added = formatMomentString(data.date_added);
		const date_edited = formatMomentString(data.date_edited);
		const accreditation_anniversary = formatMomentObject(data.accreditation_anniversary);
		const site_visit_starts = formatMomentObject(data.site_visit_starts);
		const site_visit_ends = formatMomentObject(data.site_visit_ends);
		const team_arrives = formatMomentObject(data.team_arrives);
		const team_departs = formatMomentObject(data.team_departs);

		// React doesn't like it when input elements have values of null, so set them to empty
		Object.keys(data).map(function(key) {
			if (
				data[key] === null &&
				key !== "site_visit_starts" &&
				key !== "site_visit_ends" &&
				key !== "team_arrives" &&
				key !== "team_departs" &&
				key !== "accreditation_anniversary"
			) {
				data[key] = "";
			}

			if (key === "alternate_emails" && !data[key].length) {
				data[key] = [""];
			}

			// Return something just so browser doesn't throw a warning about map not returning anything
			return data[key];
		});

		return {
			...data,
			date_added,
			date_edited,
			site_visit_starts,
			site_visit_ends,
			team_arrives,
			team_departs,
			accreditation_anniversary
		};
	};

	// ===================================================================================
	//  COMPONENT CUSTOM HANDLERS
	// ===================================================================================
	addReviewer = (reviewer, type) => {
		let newRegistered = [...this.state.data.reviewers.registered];
		const indexOf = newRegistered.findIndex(item => item.id === reviewer.id);
		newRegistered = newRegistered.slice(0, indexOf).concat(newRegistered.slice(indexOf + 1));

		this.setState({
			data: {
				...this.state.data,
				reviewers: {
					...this.state.data.reviewers,
					registered: [...newRegistered],
					[type]: this.state.data.reviewers[type].concat(reviewer)
				}
			}
		});
	};

	handleReviewerRegisterDelete = (reviewer, type) => {
		let newState = [...this.state.data.reviewers[type]];
		const indexOf = newState.findIndex(item => item.id === reviewer.id);
		newState = newState.slice(0, indexOf).concat(newState.slice(indexOf + 1));

		this.setState({
			data: {
				...this.state.data,
				reviewers: {
					...this.state.data.reviewers,
					registered: this.state.data.reviewers.registered.concat(reviewer),
					[type]: [...newState]
				}
			}
		});
	};

	handleReviewerRegisterDeleteFromRegisteredList = reviewer => {
		let newState = [...this.state.data.reviewers.registered];
		const indexOf = newState.findIndex(item => item.id === reviewer.id);
		newState = newState.slice(0, indexOf).concat(newState.slice(indexOf + 1));

		this.setState({
			data: {
				...this.state.data,
				reviewers: {
					...this.state.data.reviewers,
					registered: [...newState]
				}
			}
		});
	};

	handleInputChange = event => {
		const { id, value } = event.target;

		this.setState({
			data: {
				...this.state.data,
				[id]: value
			}
		});
	};

	// ===================================================================================
	//  SUBMIT
	// ===================================================================================
	handleSubmit = () => {
		const { user } = this.props;

		this.setState(
			{
				updateErrors: {},
				updateLoading: true,
				updateSuccess: ""
			},
			() => {
				const { data } = this.state;

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + "review/update", data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						if (response.data.status === 403) {
							this.props.userLogout("admin");
						} else if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								updateLoading: false,
								updateErrors: !isEmpty(response.data.errors)
									? response.data.errors
									: { general: response.data.message }
							});
						} else {
							// // Redirect to store edit route
							// if (!data.id) {
							// 	this.props.history.push({
							// 		pathname: "/admin/reviews/edit/" + response.data.data.id,
							// 		state: { successMessage: response.data.message }
							// 	});
							// 	return;
							// }

							// // Format record data returned from API
							let reviewRecord = typeof(response.data.review) !== 'undefined' ? this.formatRecordData(response.data.review) : null;
							console.log(reviewRecord);

							if(reviewRecord == null){
								this.setState({
									updateLoading: false,
									updateSuccess: "You have successfully created a review!"
								});
							} else {
								this.setState({
									data: reviewRecord,
									updateLoading: false,
									updateSuccess: response.data.message
								});
							}
						}
					})
					.catch(err => {
						if (err.response) {
							this.setState({
								updateLoading: false,
								updateErrors: {
									general: err.response.data.error
										? err.response.data.error
										: err.response.data.message
								}
							});
						} else {
							this.setState({
								updateLoading: false,
								updateErrors: { general: err.message }
							});
						}
					});
			}
		);
	};

	// ===================================================================================
	//  DELETE
	// ===================================================================================
	handleDelete = () => {
		const { user } = this.props;

		this.setState({
			deletionInProgress: true,
			deleteErrors: {},
			deleteSuccess: ""
		});

		const token = user.token;
		const AUTH_HEADER = { Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token };
		return axios
			.delete(process.env.REACT_APP_API_URL + "review/" + this.state.data.id, {
				headers: AUTH_HEADER
			})
			.then(response => {
				if (!!response.data.token) {
					this.props.userTokenUpdate("admin", response.data.token);
				}

				if (response.data.status === 403) {
					this.props.userLogout("admin");
				} else if (
					response.status !== 200 ||
					(response.data.status && response.data.status !== 200)
				) {
					this.setState({
						deletionInProgress: false,
						deleteErrors: !isEmpty(response.data.errors)
							? response.data.errors
							: response.data.message
					});
				} else {
					this.setState({
						deletionInProgress: false,
						deleteSuccess: response.data.message
					});
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						deletionInProgress: false,
						deleteErrors: err.response.data.error
							? err.response.data.error
							: err.response.data.message
					});
				} else {
					this.setState({
						deletionInProgress: false,
						deleteErrors: err.message
					});
				}
			});
	};

	handleDeleteRedirect() {
		this.props.history.push("/admin/reviews");
	}

	// ===================================================================================
	//  DOCUMENT UPLOADS
	// ===================================================================================
	handleUploads = () => {
		const { user } = this.props;

		// Trigger the upload start
		this.setState(
			{
				documentsInProgress: true,
				documentsError: null
			},
			() => {
				// Wait for all files to finish uploading
				Promise.all(this.state.promiseCollection).then(files => {
					// Files response is going to be a single-element array with the files within it.
					// Ex. files[0] = [file, file]
					files = files[0];

					// We might need to delete these later
					const uploadedUrls = [];
					if (!!files.length) {
						for (let i = 0; i < files.length; i++) {
							uploadedUrls[i] = pick(files[i].results, ["dirName", "filename"]);
						}
					}

					// Put request data together
					const data = {
						files: uploadedUrls,
						review_id: this.state.data.id
					};

					const token = user.token;
					const AUTH_HEADER = {
						Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
					};
					return axios
						.post(process.env.REACT_APP_API_URL + "review/document", data, {
							headers: AUTH_HEADER
						})
						.then(response => {
							if (!!response.data.token) {
								this.props.userTokenUpdate("admin", response.data.token);
							}

							// ===================================
							//  ERROR
							// ===================================
							if (
								response.status !== 200 ||
								(response.data.status && response.data.status !== 200)
							) {
								this.setState({
									promiseCollection: [],
									documentsInProgress: false,
									documentsError: !isEmpty(response.data.errors)
										? response.data.errors
										: {
												general: response.data.message
										  },
									allowRetry: true
								});

								deleteTempImages(uploadedUrls, this.props.user.token).catch(err => {
									console.log(err);
								});
							}
							// ===================================
							//  SUCCESS
							// ===================================
							else {
								// Reset state
								this.setState({
									documentsInProgress: false,
									documentsSuccess: response.data.message,
									promiseCollection: [],
									allowRetry: false,
									data: {
										...this.state.data,
										documents: [...response.data.review.documents]
									}
								});

								// Update admin token
								this.props.userTokenUpdate("admin", response.data.token);

								// Update documents in store
								this.props.updateDocuments(response.data.review.documents);
							}
						})
						.catch(err => {
							if (err.response) {
								this.setState({
									promiseCollection: [],
									documentsInProgress: false,
									documentsError: {
										general: err.response.data.error
											? err.response.data.error
											: err.response.data.message
									},
									allowRetry: true
								});
							} else {
								this.setState({
									documentsInProgress: false,
									documentsError: {
										general: err.message
									},
									allowRetry: true
								});
							}

							deleteTempImages(uploadedUrls, this.props.user.token).catch(err => {
								console.log(err);
							});
						});
				});
			}
		);
	};

	updateFileTitle = (e, fileId) => {
		const { value } = e.target;

		// Update the title value of the document object
		const newDocs = this.state.data.documents.map(file => {
			if (file.id !== fileId) {
				return file;
			}

			// Update the title, and set the "changed" status
			return {
				...file,
				title: value,
				titleChanged: true
			};
		});

		// Finally, set state
		this.setState({
			data: {
				...this.state.data,
				documents: [...newDocs]
			}
		});
	};

	/**
	 *
	 * We have to accept an object as the arguments so we can easily pass it in from a key handler, etc.
	 * .apply() doesn't work super well.  Maybe it does, but it's lame to use for every other use case.
	 *
	 */
	saveFileTitle = args => {
		const { fileIndex, fileParentItem } = args;
		// Set state of saving fileParentItem (review, group, etc)
		const savedDocState = this.state.data.documents.map((file, i) => {
			if (i !== fileIndex) {
				return file;
			}

			// Update the title, and set the "changed" status
			return { ...file, saving: true, successful: false };
		});

		this.setState(
			{
				data: {
					...this.state.data,
					documents: [...savedDocState]
				}
			},
			() => {
				const { user } = this.props;
				const data = {
					...fileParentItem,
					fileIndex
				};
				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + "review/document-update", data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}
						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								showErrorDialog: true,
								dialogErrors: response.data.errors
							});
							this.resetFileSaving(fileIndex);
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							// Reset saving status for fileParentItem
							this.resetFileSaving(fileIndex, true);
						}
					})
					.catch(err => {
						this.setState({
							showErrorDialog: true,
							dialogErrors: err.toString()
						});
						this.resetFileSaving(fileIndex);
					});
			}
		);
	};

	resetFileSaving = (fileIndex, successful = false) => {
		this.setState({
			data: {
				...this.state.data,
				documents: this.state.data.documents.map((file, i) => {
					if (i !== fileIndex) {
						return file;
					}

					// Update the title, and set the "changed" status
					return {
						...file,
						saving: false,
						successful,
						titleChanged: !successful
					};
				})
			}
		});
	};

	deleteFile = fileId => {
		this.setState(
			{
				deleteItem: {
					...this.state.deleteItem,
					deletionInProgress: true
				}
			},
			() => {
				const { user } = this.props;

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.delete(process.env.REACT_APP_API_URL + `review/document/${fileId}`, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								deleteItem: {
									...this.state.deleteItem,
									deletionInProgress: false,
									deleteErrors: { ...response.data.errors }
								}
							});
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							this.setState(
								{
									deleteFileSuccess: response.data.message,
									deleteItem: {},
									data: {
										...this.state.data,
										documents: [...response.data.review.documents]
									}
								},
								() => {
									// Update documents in store
									this.props.updateDocuments(response.data.review.documents);
								}
							);
						}
					})
					.catch(err => {
						this.setState({
							deleteItem: {
								...this.state.deleteItem,
								deletionInProgress: false,
								deleteErrors: err.toString()
							}
						});
					});
			}
		);
	};

	onSortEndFiles = (oldIndex, newIndex) => {
		this.setState(
			{
				documentsError: null,
				data: {
					...this.state.data,
					documents: arrayMove(this.state.data.documents, oldIndex, newIndex)
				}
			},
			() => {
				const { user } = this.props;

				const data = {
					review_id: this.state.data.id,
					files: this.state.data.documents
				};

				const token = user.token;
				const AUTH_HEADER = {
					Authorization: process.env.REACT_APP_SITE_KEY + ":Bearer " + token
				};
				return axios
					.post(process.env.REACT_APP_API_URL + `review/document-sort`, data, {
						headers: AUTH_HEADER
					})
					.then(response => {
						if (!!response.data.token) {
							this.props.userTokenUpdate("admin", response.data.token);
						}

						// ===================================
						//  ERROR
						// ===================================
						if (
							response.status !== 200 ||
							(response.data.status && response.data.status !== 200)
						) {
							this.setState({
								documentsError: response.data.errors
							});
						}
						// ===================================
						//  SUCCESS
						// ===================================
						else {
							// this.setState({
							// 	...this.state.data,
							// 	documents: [...newDocs]
							// });
						}
					})
					.catch(err => {
						this.setState({
							documentsError: err
						});
					});
			}
		);
	};

	// ===================================================================================
	//  RENDER
	// ===================================================================================
	render() {
		const {
			organizationsLoading,
			organizationsError,
			organizations,
			sectorsLoading,
			sectorsError,
			sectors
		} = this.props;
		const { data, addSucccess, updateErrors, updateSuccess, updateLoading } = this.state;
		// console.log("here is the value for the reviewers global state ", this.props.reviewers);
		return (
			<React.Fragment>
				{!this.props.match.params.id && <h1>Add Review</h1>}

				{/* Manager Alert - Won't appear for admins */}
				<ManagersAlert />

				{/* Loading */}
				{this.state.isLoading ? (
					<GeneralLoader />
				) : (
					<React.Fragment>
						{/* Success */}
						{!!addSucccess && <SuccessWrap animate={1}>{addSucccess}</SuccessWrap>}

						{/* General error (record load error) */}
						{!!this.props.recordError && (
							<ErrorWrap animate={1}>{this.props.recordError}</ErrorWrap>
						)}

						{/* Record not found */}
						{!!data.id && !data.organization_id && (
							<ErrorWrap animate={1}>{"Unable to find record"}</ErrorWrap>
						)}

						{/* Show form if adding new item, or item was selected and found */}
						{(!data.id || (data.id && data.organization_id)) && (
							<React.Fragment>
								<ReviewForm
									// data={data}
									data={data}
									role={this.props.role}
									// reviewerData={this.props.reviewer}
									errors={updateErrors}
									inProcess={updateLoading}
									organizations={organizations}
									organizationsLoading={organizationsLoading}
									organizationsError={organizationsError}
									sectors={sectors}
									sectorsLoading={sectorsLoading}
									sectorsError={sectorsError}
									handleDateChange={(e, id) =>
										handleDateChange(e, id, this.state).then(res =>
											this.setState(res)
										)
									}
									handleKeyPress={e => handleKeyPress(e, this.handleSubmit)}
									handleInputChange={this.handleInputChange}
									updateSelectedValue={(value, id) => {
										updateSelectedValue(value, id, this.state).then(res =>
											this.setState(res)
										);
									}}
									handleCheckboxClickArray={(obj, name) =>
										handleCheckboxClickArray(obj, name, this.state).then(res =>
											this.setState(res)
										)
									}
									handleCheckboxClick={e =>
										handleCheckboxClick(e, this.state).then(res =>
											this.setState(res)
										)
									}
									editorChange={(id, e) =>
										editorChange(id, e, this.state).then(res =>
											this.setState(res)
										)
									}
									addReviewer={this.addReviewer}
									handleReviewerRegisterDelete={this.handleReviewerRegisterDelete}
									handleReviewerRegisterDeleteFromRegisteredList={
										this.handleReviewerRegisterDeleteFromRegisteredList
									}
									// Document Uploads
									documentsInProgress={this.state.documentsInProgress}
									documentsError={this.state.documentsError}
									documentsSuccess={this.state.documentsSuccess}
									handleDocumentPromisePush={promise =>
										this.state.promiseCollection.push(promise)
									}
									handleFilesReadyToUpload={this.handleUploads}
									retryHandler={this.handleUploads}
									allowRetry={this.state.allowRetry}
									saveFileTitle={this.saveFileTitle}
									updateFileTitle={this.updateFileTitle}
									handleKeyPressFile={(e, fileIndex) =>
										handleKeyPress(e, this.saveFileTitle, {
											fileParentItem: data,
											fileIndex
										})
									}
									showFileDeleteConfirmationDialog={
										this.state.showFileDeleteConfirmationDialog
									}
									deleteItem={this.state.deleteItem}
									handleDelete={this.state.deleteHandler}
									deleteFileSuccess={this.state.deleteFileSuccess}
									deleteFile={file =>
										this.setState({
											showFileDeleteConfirmationDialog: true,
											deleteHandler: this.deleteFile,
											deleteItem: file
										})
									}
									handleFileDeleteDialogClose={() =>
										this.setState({
											showFileDeleteConfirmationDialog: false,
											deleteFileSuccess: null,
											deleteItem: {}
										})
									}
									onSortEndFiles={(oldIndex, newIndex) =>
										this.onSortEndFiles(oldIndex, newIndex)
									}
								/>

								{this.props.role === 'admin' &&
									<div className="btn_container">
										{!!updateErrors.general && (
											<ErrorWrap animate={1}>{updateErrors.general}</ErrorWrap>
										)}

										{updateSuccess && <SuccessWrap>{updateSuccess}</SuccessWrap>}

										<Button
											text={!!data.id ? "Update" : "Create"}
											clickHandler={this.handleSubmit}
											isLoading={updateLoading}
										/>
										{!!data.id && (
											<Button
												text={"Delete"}
												classes={"delete-btn"}
												clickHandler={() =>
													this.setState({
														showDeleteConfirmationDialog: true
													})
												}
												isLoading={updateLoading}
											/>
										)}
									</div>
								}
								<ErrorDialog
									show={this.state.showErrorDialog}
									errors={this.state.dialogErrors}
									handleClose={() => this.setState({ showErrorDialog: false })}
								/>
								<DeleteConfirmationDialog
									show={this.state.showDeleteConfirmationDialog}
									deletionInProgress={this.state.deletionInProgress}
									id={data.id}
									itemTitle={data.organization_title}
									handleDelete={this.handleDelete.bind(this)}
									handleDeleteRedirect={this.handleDeleteRedirect.bind(this)}
									handleClose={() =>
										this.setState({ showDeleteConfirmationDialog: false })
									}
									deleteErrors={this.state.deleteErrors}
									deleteSuccess={this.state.deleteSuccess}
								/>
							</React.Fragment>
						)}
					</React.Fragment>
				)}
			</React.Fragment>
		);
	}
}

const mapStateToProps = state => {
	return {
		user: state.user.admin,
		role: state.user.admin.user.role,
		recordError: state.reviews.errors,
		isLoading: state.reviews.isLoading !== undefined ? state.reviews.isLoading : 1,
		organizationsLoading:
			state.organizations.isLoading !== undefined ? state.organizations.isLoading : 1,
		organizationsError: state.organizations.error,
		organizations: state.organizations.organizations,
		sectors: state.sectors.sectors,
		sectorsLoading:
			state.sectors.isLoading !== undefined
				? state.sectors.isLoading
				: state.sectors.isLoading,
		sectorsError: state.sectors.error
		// reviewers: state
	};
};

export default connect(
	mapStateToProps,
	{
		setpageTitle,
		userTokenUpdate,
		userLogout,
		getReviewRecord,
		updateDocuments,
		getSectors,
		getOrganizations
	}
)(AdminReviewsForm);



// WEBPACK FOOTER //
// ./src/components/admin/reviews/AdminReviewsForm.js